import { employedTypes } from '@thesavingsgroup/constants/employedTypes';
import { unemployedTypes } from '@thesavingsgroup/constants/unemployedTypes';
import { EmploymentType } from '@thesavingsgroup/enums/EmploymentType';
import { HttpStatusCode } from '@thesavingsgroup/enums/HttpStatusCode';
import { isNonEmptyString } from '@thesavingsgroup/utils/isNonEmptyString';
import i18n from 'i18next';
import { isString } from 'lodash';
import { StateCreator } from 'zustand';

import { ApplicationSubmittingAnalyticsParams } from '../analyticStore/types/ApplicationSubmittingAnalyticsParams';
import { DEFAULT_AUTH_PATH } from '../routeStore/constants/authPath';
import { additionalIncomeDurationFormat } from './constants/additionalIncomeDurationFormat';
import { additionalIncomeTypeFormat } from './constants/additionalIncomeTypeFormat';
import { employmentTypeFormat } from './constants/employmentTypeFormat';
import { HALF_YEAR_IN_MONTHS_AS_STRING } from './constants/halfYear';
import { ONE_YEAR_IN_MONTHS_AS_STRING } from './constants/oneYear';
import { residenceTypeFormat } from './constants/residenceTypeFormat';
import { TWO_YEARS_IN_MONTHS_AS_STRING } from './constants/twoYears';
import { ApplicantDto } from './dto/ApplicantDto';
import { UserIncomesDto } from './dto/UserIncomesDto';
import { UserResidencesDto } from './dto/UserResidencesDto';
import { IncomeFrequencyEnum } from './enums/IncomeFrequencyEnum';
import { IncomeTypeEnum } from './enums/IncomeTypeEnum';
import { ResidenceTypeEnum } from './enums/ResidenceTypeEnum';
import { getSubmitApplicationErrorMessage } from './functions/getSubmitApplicationErrorMessage';
import { toApplicantState } from './functions/toApplicantState';
import { toApplicantStateFromIncomes } from './functions/toApplicantStateFromIncomes';
import { toApplicantStateFromResidences } from './functions/toApplicantStateFromResidences';
import { toUserIncomesDto } from './functions/toUserIncomesDto';
import { toUserResidencesDto } from './functions/toUserResidencesDto';
import { ApplicantState } from './types/ApplicantState';
import { ApplicantStateDependencies } from './types/ApplicantStateDependencies';
import { NO, YES, YesOrNo } from './types/YesOrNo';

export const keysToPersist: string[] = [
  'addCoapplicant',
  'currentIncomeId',
  'currentResidenceId',
  'hasAdditionalIncome',
  'isCurrentlyEmployed',
  'previousIncomeId',
  'previousResidenceId',
];

export const currentIncomeKeys = [
  'currentIncomeEmployerName',
  'currentIncomeEmploymentType',
  'currentIncomeGrossAnnual',
  'currentIncomeJobTitle',
  'currentIncomeMonthsAt',
];

export const currentResidenceKeys = [
  'currentResidenceCity',
  'currentResidenceLineOne',
  'currentResidenceLineTwo',
  'currentResidenceMonthlyPaymentAmount',
  'currentResidenceMonthsAt',
  'currentResidencePostalCode',
  'currentResidenceState',
  'currentResidenceType',
];

export const previousIncomeKeys = [
  'previousIncomeEmployerName',
  'previousIncomeEmploymentType',
  'previousIncomeGrossAnnual',
  'previousIncomeJobTitle',
];

export const previousResidenceKeys = [
  'previousResidenceCity',
  'previousResidenceLineOne',
  'previousResidenceLineTwo',
  'previousResidencePostalCode',
  'previousResidenceState',
];

export const applicantStore =
  ({
    resetStore,
    useAnalyticStore,
    useApiStore,
    useAttributionStore,
    useCoapplicantStore,
    useCookieStore,
    useDashboardStore,
    useUserStore,
    useWorkflowStore,
  }: ApplicantStateDependencies): StateCreator<ApplicantState> =>
  (set, get) => ({
    addCoapplicant: null,

    additionalIncomeAmount: null,
    additionalIncomeDurationMonths: null,
    additionalIncomeDurationYears: null,
    additionalIncomeEmployerName: null,
    additionalIncomeFrequency: null,
    additionalIncomeId: null,
    additionalIncomeJobTitle: null,
    additionalIncomeType: null,

    currentIncomeEmployerName: null,
    currentIncomeEmploymentType: null,
    currentIncomeGrossAnnual: null,
    currentIncomeId: null,
    currentIncomeJobTitle: null,
    currentIncomeMonthsAt: null,

    currentResidenceCity: null,
    currentResidenceId: null,
    currentResidenceLineOne: null,
    currentResidenceLineTwo: null,
    currentResidenceMonthlyPaymentAmount: null,
    currentResidenceMonthsAt: null,
    currentResidencePostalCode: null,
    currentResidenceState: null,
    currentResidenceType: null,

    dateOfBirth: null,
    disableCoapplicantFailedMessage: null,

    hasAdditionalIncome: null,
    hasLoadedApplicant: false,

    isCurrentlyEmployed: null,
    isDisablingCoapplicant: false,
    isLoadingApplicant: false,
    isSubmittingApplication: false,
    isSyncingIncome: false,
    isSyncingResidence: false,

    previousIncomeEmployerName: null,
    previousIncomeEmploymentType: null,
    previousIncomeGrossAnnual: null,
    previousIncomeId: null,
    previousIncomeJobTitle: null,

    previousResidenceCity: null,
    previousResidenceId: null,
    previousResidenceLineOne: null,
    previousResidenceLineTwo: null,
    previousResidencePostalCode: null,
    previousResidenceState: null,

    retryFailedSubmissionFailedMessage: null,
    retryFailedSubmissionIsLoading: false,

    syncingIncomeFailedMessage: null,
    syncingResidenceFailedMessage: null,

    submitApplicationFailedMessage: null,

    computed: {
      get additionalIncomeAmount(): string {
        const { additionalIncomeAmount } = get();

        if (!additionalIncomeAmount) {
          return '-';
        }

        const localeAmount = parseInt(
          additionalIncomeAmount,
          10,
        ).toLocaleString();

        return `$${localeAmount}`;
      },
      get additionalIncomeFrequency(): string {
        const { additionalIncomeFrequency } = get();

        if (
          additionalIncomeFrequency &&
          additionalIncomeDurationFormat[additionalIncomeFrequency]
        ) {
          return additionalIncomeDurationFormat[additionalIncomeFrequency];
        }

        return '-';
      },
      get additionalIncomeStatus(): string {
        const { hasAdditionalIncome } = get();

        return hasAdditionalIncome === YES
          ? i18n.t('incomeStatus.hasAdditionalIncome')
          : i18n.t('incomeStatus.noAdditionalIncome');
      },
      get additionalIncomeType(): string {
        const { additionalIncomeType } = get();

        if (
          additionalIncomeType &&
          additionalIncomeTypeFormat[additionalIncomeType]
        ) {
          return additionalIncomeTypeFormat[additionalIncomeType];
        }

        return '-';
      },
      get currentIncomeAmount(): string {
        const { currentIncomeGrossAnnual } = get();

        if (!currentIncomeGrossAnnual) {
          return '-';
        }

        const localeAmount = parseInt(
          currentIncomeGrossAnnual,
          10,
        ).toLocaleString();

        return `$${localeAmount}`;
      },
      get currentIncomeDuration(): string {
        const { currentIncomeMonthsAt } = get();

        if (!currentIncomeMonthsAt) {
          return '-';
        }

        return currentIncomeMonthsAt === ONE_YEAR_IN_MONTHS_AS_STRING
          ? i18n.t('employmentDuration.moreThan1Year')
          : i18n.t('employmentDuration.lessThan1Year');
      },
      get currentIncomeStatus(): string {
        const { isCurrentlyEmployed } = get();

        if (!isCurrentlyEmployed) {
          return '-';
        }

        return isCurrentlyEmployed === YES
          ? i18n.t('employmentStatus.employed')
          : i18n.t('employmentStatus.unemployed');
      },
      get currentIncomeType(): string {
        const { currentIncomeEmploymentType } = get();

        if (
          currentIncomeEmploymentType &&
          employmentTypeFormat[currentIncomeEmploymentType]
        ) {
          return employmentTypeFormat[currentIncomeEmploymentType];
        }

        return '-';
      },
      get currentResidenceAddress(): string {
        const {
          currentResidenceCity,
          currentResidenceLineOne,
          currentResidenceLineTwo,
          currentResidencePostalCode,
          currentResidenceState,
        } = get();

        if (
          !currentResidenceLineOne ||
          !currentResidenceCity ||
          !currentResidenceState ||
          !currentResidencePostalCode
        ) {
          return '-';
        }

        let addressFormatted = '';

        if (currentResidenceLineOne) {
          addressFormatted += currentResidenceLineOne;
        }

        if (currentResidenceLineTwo) {
          addressFormatted += `, ${currentResidenceLineTwo}`;
        }

        if (currentResidenceCity) {
          addressFormatted += `, ${currentResidenceCity}`;
        }

        if (currentResidenceState) {
          addressFormatted += `, ${currentResidenceState}`;
        }

        if (currentResidencePostalCode) {
          addressFormatted += `, ${currentResidencePostalCode}`;
        }

        return addressFormatted;
      },
      get currentResidenceAmount(): string {
        const { currentResidenceMonthlyPaymentAmount } = get();

        if (!currentResidenceMonthlyPaymentAmount) {
          return '-';
        }

        const localeAmount = parseInt(
          currentResidenceMonthlyPaymentAmount,
          10,
        ).toLocaleString();

        return i18n.t('currency.aMonth', { amount: localeAmount });
      },
      get currentResidenceCityAsString(): string {
        const { currentResidenceCity } = get();

        return isNonEmptyString(currentResidenceCity)
          ? currentResidenceCity
          : '';
      },
      get currentResidenceDuration(): string {
        const { currentResidenceMonthsAt } = get();

        if (!currentResidenceMonthsAt) {
          return '-';
        }

        return currentResidenceMonthsAt === TWO_YEARS_IN_MONTHS_AS_STRING
          ? i18n.t('residenceDuration.moreThan2Years')
          : i18n.t('residenceDuration.lessThan2Years');
      },
      get currentResidenceLineOneAsString(): string {
        const { currentResidenceLineOne } = get();

        return isNonEmptyString(currentResidenceLineOne)
          ? currentResidenceLineOne
          : '';
      },
      get currentResidenceLineTwoAsString(): string {
        const { currentResidenceLineTwo } = get();

        return isNonEmptyString(currentResidenceLineTwo)
          ? currentResidenceLineTwo
          : '';
      },
      get currentResidencePostalCodeAsString(): string {
        const { currentResidencePostalCode } = get();

        return isNonEmptyString(currentResidencePostalCode)
          ? currentResidencePostalCode
          : '';
      },
      get currentResidenceStateAsString(): string {
        const { currentResidenceState } = get();

        return isNonEmptyString(currentResidenceState)
          ? currentResidenceState
          : '';
      },
      get currentResidenceType(): string {
        const { currentResidenceType } = get();

        if (currentResidenceType && residenceTypeFormat[currentResidenceType]) {
          return residenceTypeFormat[currentResidenceType];
        }

        return '-';
      },
      get employedGrossAnnualIncome(): string | null {
        const { currentIncomeEmploymentType, currentIncomeGrossAnnual } = get();

        if (
          currentIncomeEmploymentType &&
          employedTypes.includes(currentIncomeEmploymentType) &&
          currentIncomeGrossAnnual
        ) {
          return currentIncomeGrossAnnual;
        }

        return null;
      },
      get isAdditionalIncomePersisted(): boolean {
        const { additionalIncomeId } = get();
        return isString(additionalIncomeId) && additionalIncomeId.length !== 0;
      },
      get isCurrentIncomeEmployedDetailsCompleted(): boolean {
        const {
          isCurrentlyEmployed,
          currentIncomeEmploymentType,
          currentIncomeEmployerName,
          currentIncomeJobTitle,
          currentIncomeGrossAnnual,
        } = get();

        return Boolean(
          isCurrentlyEmployed &&
            currentIncomeEmploymentType &&
            currentIncomeEmployerName &&
            currentIncomeJobTitle &&
            currentIncomeGrossAnnual,
        );
      },
      get isCurrentIncomeLessThan1Year(): boolean {
        return get().currentIncomeMonthsAt === HALF_YEAR_IN_MONTHS_AS_STRING;
      },
      get isCurrentIncomeMoreThan1Year(): boolean {
        return get().currentIncomeMonthsAt === ONE_YEAR_IN_MONTHS_AS_STRING;
      },
      get isCurrentIncomePersisted(): boolean {
        const { currentIncomeId } = get();
        return isString(currentIncomeId) && currentIncomeId.length !== 0;
      },
      get isCurrentIncomeUnemployedDetailsCompleted(): boolean {
        const {
          isCurrentlyEmployed,
          currentIncomeEmploymentType,
          currentIncomeGrossAnnual,
        } = get();

        return Boolean(
          isCurrentlyEmployed &&
            currentIncomeEmploymentType &&
            currentIncomeGrossAnnual,
        );
      },
      get isCurrentResidenceDetailsCompleted(): boolean {
        const {
          currentResidenceType,
          currentResidenceLineOne,
          currentResidenceCity,
          currentResidenceState,
          currentResidencePostalCode,
          currentResidenceMonthlyPaymentAmount,
        } = get();

        return Boolean(
          currentResidenceType &&
            currentResidenceLineOne &&
            currentResidenceCity &&
            currentResidenceState &&
            currentResidencePostalCode &&
            currentResidenceMonthlyPaymentAmount,
        );
      },
      get isCurrentResidenceLessThan2Years(): boolean {
        return get().currentResidenceMonthsAt === ONE_YEAR_IN_MONTHS_AS_STRING;
      },
      get isCurrentResidenceMoreThan2Years(): boolean {
        return get().currentResidenceMonthsAt === TWO_YEARS_IN_MONTHS_AS_STRING;
      },
      get isCurrentResidencePersisted(): boolean {
        const { currentResidenceId } = get();
        return isString(currentResidenceId) && currentResidenceId.length !== 0;
      },
      get isPreviousIncomePersisted(): boolean {
        const { previousIncomeId } = get();
        return isString(previousIncomeId) && previousIncomeId.length !== 0;
      },
      get isPreviousResidencePersisted(): boolean {
        const { previousResidenceId } = get();
        return (
          isString(previousResidenceId) && previousResidenceId.length !== 0
        );
      },
      get reviewEditPathname(): string {
        const { vehicleId } = useDashboardStore.getState().computed;

        return `/applicant/${vehicleId}/review-applicant`;
      },
      get reviewEditProgress(): number {
        return useWorkflowStore
          .getState()
          .progressByPath(/\/applicant\/.*\/review-applicant/);
      },
      get unemployedGrossAnnualIncome(): string | null {
        const { currentIncomeGrossAnnual, currentIncomeEmploymentType } = get();

        if (
          currentIncomeEmploymentType &&
          unemployedTypes.includes(currentIncomeEmploymentType) &&
          currentIncomeGrossAnnual
        ) {
          return currentIncomeGrossAnnual;
        }

        return null;
      },
    },

    clearCurrentEmployment(): void {
      set({
        currentIncomeEmployerName: null,
        currentIncomeEmploymentType: null,
        currentIncomeGrossAnnual: null,
        currentIncomeJobTitle: null,
      });
    },

    clearSubmitApplicationFailure(): void {
      set({ submitApplicationFailedMessage: null });
    },

    disableCoapplicant(): Promise<boolean> {
      const {
        computed: { vehicleApplicationId, vehicleId },
      } = useDashboardStore.getState();

      set({
        disableCoapplicantFailedMessage: null,
        isDisablingCoapplicant: true,
      });

      const portalHttpRequest = useApiStore.getState().portalApi();

      return portalHttpRequest
        .delete(
          `/2.0/vehicles/${vehicleId}/application/${vehicleApplicationId}/co-applicant`,
        )
        .then(() => {
          resetStore(useCoapplicantStore);

          return true;
        })
        .catch((error: unknown) => {
          console.error(error);

          set({
            disableCoapplicantFailedMessage: i18n.t(
              'errors.disableCoapplicant.defaultMessage',
            ),
          });

          return false;
        })
        .finally(() => {
          set({ isDisablingCoapplicant: false });
        });
    },

    loadApplicant(): Promise<null> {
      const { hasLoadedApplicant, isLoadingApplicant } = get();

      if (hasLoadedApplicant || isLoadingApplicant) {
        return Promise.resolve(null);
      }

      set({ isLoadingApplicant: true });

      const portalHttpRequest = useApiStore.getState().portalApi();

      return portalHttpRequest
        .get<never, ApplicantDto>('/2.0/applicant')
        .then((applicant: ApplicantDto) => {
          set(toApplicantState(applicant));
        })
        .then(() => null)
        .catch((error: unknown) => {
          console.error(error);
          return null;
        })
        .finally(() => {
          set({ hasLoadedApplicant: true, isLoadingApplicant: false });
        });
    },
    async loader(params: Record<string, string>): Promise<Response | null> {
      const { addCoapplicant, loadApplicant, setAddCoapplicant } = get();
      const { loadCoapplicant } = useCoapplicantStore.getState();

      const isVehicleEmpty =
        useDashboardStore.getState().computed.isVehicleEmpty;

      // Fetch vehicle if it's not already loaded
      if (isVehicleEmpty) {
        await useDashboardStore.getState().fetchVehicleById(params.vehicleId);
      }

      const fetchVehicleByIdFailed =
        useDashboardStore.getState().fetchVehicleByIdFailedMessage;

      // Redirect to default authenticated path if vehicle fetch failed
      if (fetchVehicleByIdFailed) {
        const redirectUrl = new URL(DEFAULT_AUTH_PATH, window.location.origin);

        return Response.redirect(redirectUrl.toString(), HttpStatusCode.FOUND);
      }

      // Fetch applicant data if it's not already loaded
      await loadApplicant();

      const {
        computed: {
          vehicleApplicationHasCoapplicant,
          vehicleApplicationAutopayNumber,
        },
      } = useDashboardStore.getState();

      const coapplicantSelectionNotMade = addCoapplicant === null;

      // Set co-applicant selection based on existing application data
      // Only set if user hasn't already made a selection
      if (coapplicantSelectionNotMade) {
        // Fetch coapplicant data if it's not already loaded
        if (vehicleApplicationHasCoapplicant) {
          setAddCoapplicant(YES);

          await loadCoapplicant();
        } else if (
          // if we have no co-applicant and the application has an AP number
          // we can assume the user does not (currently) want a co-applicant
          vehicleApplicationHasCoapplicant === false &&
          vehicleApplicationAutopayNumber
        ) {
          setAddCoapplicant(NO);
        }
      }

      return null;
    },

    retryFailedSubmission(vehicleId: string | null | undefined): Promise<void> {
      if (!vehicleId) {
        return Promise.resolve();
      }

      set({ retryFailedSubmissionIsLoading: true });

      const portalHttpRequest = useApiStore.getState().portalApi();

      return portalHttpRequest
        .post('/2.0/applicant/retry-failed-submission', { vehicleId })
        .then(() => {
          window.location.reload();
        })
        .catch(() => {
          set({
            retryFailedSubmissionFailedMessage: i18n.t(
              'errors.retryFailedSubmission.defaultMessage',
            ),
            retryFailedSubmissionIsLoading: false,
          });
        });
    },

    setAddCoapplicant(value: YesOrNo): void {
      set({ addCoapplicant: value });
    },

    setAdditionalIncomeAmount(value: string | null): void {
      set({ additionalIncomeAmount: value });
    },
    setAdditionalIncomeDurationMonths(value: string | null): void {
      set({ additionalIncomeDurationMonths: value });
    },
    setAdditionalIncomeDurationYears(value: string | null): void {
      set({ additionalIncomeDurationYears: value });
    },
    setAdditionalIncomeEmployerName(value: string | null): void {
      set({ additionalIncomeEmployerName: value });
    },
    setAdditionalIncomeFrequency(value: IncomeFrequencyEnum | null): void {
      set({ additionalIncomeFrequency: value });
    },
    setAdditionalIncomeJobTitle(value: string | null): void {
      set({ additionalIncomeJobTitle: value });
    },
    setAdditionalIncomeType(value: IncomeTypeEnum | null): void {
      set({ additionalIncomeType: value });
    },

    setCurrentIncomeEmployerName(value: string | null): void {
      set({ currentIncomeEmployerName: value });
    },
    setCurrentIncomeEmploymentType(value: EmploymentType | null): void {
      set({ currentIncomeEmploymentType: value });
    },
    setCurrentIncomeGrossAnnual(value: string): void {
      set({ currentIncomeGrossAnnual: value });
    },
    setCurrentIncomeJobTitle(value: string | null): void {
      set({ currentIncomeJobTitle: value });
    },
    setCurrentIncomeMonthsAt(value: string): void {
      set({
        currentIncomeMonthsAt: value,

        ...(value === ONE_YEAR_IN_MONTHS_AS_STRING && {
          previousIncomeEmployerName: null,
          previousIncomeEmploymentType: null,
          previousIncomeGrossAnnual: null,
          previousIncomeJobTitle: null,
        }),
      });
    },

    setCurrentResidenceCity(value: string): void {
      set({ currentResidenceCity: value });
    },
    setCurrentResidenceLineOne(value: string): void {
      set({ currentResidenceLineOne: value });
    },
    setCurrentResidenceLineTwo(value: string): void {
      set({ currentResidenceLineTwo: value });
    },
    setCurrentResidenceMonthlyPaymentAmount(value: string): void {
      set({ currentResidenceMonthlyPaymentAmount: value });
    },
    setCurrentResidenceMonthsAt(value: string): void {
      set({
        currentResidenceMonthsAt: value,

        ...(value === TWO_YEARS_IN_MONTHS_AS_STRING && {
          previousResidenceCity: null,
          previousResidenceLineOne: null,
          previousResidenceLineTwo: null,
          previousResidencePostalCode: null,
          previousResidenceState: null,
        }),
      });
    },
    setCurrentResidencePostalCode(value: string): void {
      set({ currentResidencePostalCode: value });
    },
    setCurrentResidenceState(value: string): void {
      set({ currentResidenceState: value });
    },
    setCurrentResidenceType(value: ResidenceTypeEnum): void {
      set({ currentResidenceType: value });
    },

    setDateOfBirth(value: string): void {
      set({ dateOfBirth: value });
    },

    setHasAdditionalIncome(value: YesOrNo): void {
      set({
        hasAdditionalIncome: value,

        ...(value === NO && {
          additionalIncomeAmount: null,
          additionalIncomeFrequency: null,
          additionalIncomeType: null,
        }),
      });
    },
    setIsCurrentlyEmployed(value: YesOrNo): void {
      set({ isCurrentlyEmployed: value });
    },

    setPreviousIncomeEmployerName(value: string | null): void {
      set({ previousIncomeEmployerName: value });
    },
    setPreviousIncomeEmploymentType(value: EmploymentType | null): void {
      set({ previousIncomeEmploymentType: value });
    },
    setPreviousIncomeGrossAnnual(value: string | null): void {
      set({ previousIncomeGrossAnnual: value });
    },
    setPreviousIncomeJobTitle(value: string | null): void {
      set({ previousIncomeJobTitle: value });
    },

    setPreviousResidenceCity(value: string | null): void {
      set({ previousResidenceCity: value });
    },
    setPreviousResidenceLineOne(value: string | null): void {
      set({ previousResidenceLineOne: value });
    },
    setPreviousResidenceLineTwo(value: string | null): void {
      set({ previousResidenceLineTwo: value });
    },
    setPreviousResidencePostalCode(value: string | null): void {
      set({ previousResidencePostalCode: value });
    },
    setPreviousResidenceState(value: string | null): void {
      set({ previousResidenceState: value });
    },

    submitApplication(
      primaryApplicantSSN: string,
      coApplicantSSN?: string,
    ): Promise<boolean> {
      const { dateOfBirth, currentResidencePostalCode } = get();
      const { coapplicantDateOfBirth } = useCoapplicantStore.getState();
      const {
        computed: { vehicleId },
      } = useDashboardStore.getState();

      const {
        computed: { attributionsForLeadChannel, initialURLForLeadChannel },
      } = useAttributionStore.getState();

      const {
        computed: { googleVisitorId },
      } = useCookieStore.getState();

      const {
        computed: {
          userEmailAddress,
          userFirstName,
          userLastName,
          userPhoneNumber,
        },
      } = useUserStore.getState();

      const {
        sendApplicationFailedImportAnalytics,
        sendApplicationSubmittedAnalytics,
        sendApplicationSubmittingAnalytics,
      } = useAnalyticStore.getState();

      const portalHttpRequest = useApiStore.getState().portalApi();

      set({
        isSubmittingApplication: true,
        submitApplicationFailedMessage: null,
      });

      const params: ApplicationSubmittingAnalyticsParams = {
        email: userEmailAddress,
        firstName: userFirstName,
        lastName: userLastName,
        phone: userPhoneNumber,
        zipCode: currentResidencePostalCode,
      };

      sendApplicationSubmittingAnalytics(params);

      return portalHttpRequest
        .post<never, null>('/2.0/applicant/submit', {
          attributions: attributionsForLeadChannel,
          birthDate: dateOfBirth,
          coApplicantBirthDate: coapplicantDateOfBirth,
          coApplicantSSN,
          googleVisitorId,
          initialURL: initialURLForLeadChannel,
          primaryApplicantSSN,
          vehicleId,
        })
        .then(() => {
          sendApplicationSubmittedAnalytics();

          return true;
        })
        .catch((error: unknown) => {
          console.error(error);

          sendApplicationFailedImportAnalytics();

          set({
            submitApplicationFailedMessage:
              getSubmitApplicationErrorMessage(error),
          });

          return false;
        })
        .finally(() => {
          set({ isSubmittingApplication: false });
        });
    },

    syncIncome(): Promise<boolean> {
      // Sync the income data with the API
      set({
        isSyncingIncome: true,
        syncingIncomeFailedMessage: null,
      });

      const portalHttpRequest = useApiStore.getState().portalApi();

      return portalHttpRequest
        .post<never, UserIncomesDto>('/2.0/income', toUserIncomesDto(get()))
        .then((incomes: UserIncomesDto) => {
          set(toApplicantStateFromIncomes(incomes));

          return true;
        })
        .catch((error: unknown) => {
          console.error(error);

          set({
            syncingIncomeFailedMessage: i18n.t(
              'errors.syncIncome.defaultMessage',
            ),
          });

          return false;
        })
        .finally(() => {
          set({ isSyncingIncome: false });
        });
    },

    syncResidence(): Promise<boolean> {
      set({
        isSyncingResidence: true,
        syncingResidenceFailedMessage: null,
      });

      const portalHttpRequest = useApiStore.getState().portalApi();

      return portalHttpRequest
        .post<never, UserResidencesDto>(
          '/2.0/residence',
          toUserResidencesDto(get()),
        )
        .then((residences: UserResidencesDto) => {
          set(toApplicantStateFromResidences(residences));

          return true;
        })
        .catch((error: unknown) => {
          console.error(error);

          set({
            syncingResidenceFailedMessage: i18n.t(
              'errors.syncResidence.defaultMessage',
            ),
          });

          return false;
        })
        .finally(() => {
          set({ isSyncingResidence: false });
        });
    },
  });
